import type { MouseEvent, PropsWithChildren } from "react";
import { css, styled } from "styled-components";

export type DropdownItemProps = PropsWithChildren & {
  index?: number;
  isActive?: boolean;
  isDisabled?: boolean;
  isProItem?: boolean;
  onClick?: (event: MouseEvent) => void;
};

const getTransition = (index: number) => `
  opacity 0.3s ${0.15 + 0.05 * index}s ease,
  transform 0.3s ${0.15 + 0.05 * index}s ease
`;

const commonDropdownStyles = css<{ index: number }>`
  border: none;
  text-align: right;
  width: 100%;
  font-size: 16px;
  white-space: nowrap;
  position: relative;
  padding: 1rem;

  transition:
    ${({ index }) => getTransition(index)},
    background-color 0.3s;
`;

const StyledProContainer = styled.button<{ index: number; isDisabled?: boolean }>`
  ${commonDropdownStyles}

  background-color: ${({ theme }) => theme.colors.proLabelBackground};
  color: ${({ theme }) => theme.colors.proLabelText}AA;
  cursor: pointer;

  &:focus-visible,
  &:hover {
    background-color: ${({ theme }) => theme.colors.proLabelBackgroundActive};
    color: ${({ theme }) => theme.colors.proLabelText};
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

const StyledContainer = styled.button<{ index: number; isActive: boolean; isDisabled?: boolean }>`
  ${commonDropdownStyles}

  transition:
    ${({ index }) => getTransition(index)},
    background-color 0.3s;
  background-color: ${({ theme }) => theme.colors.dropdownBackground};
  color: ${({ theme }) => theme.colors.textFade};

  ${({ theme, isDisabled }) =>
    isDisabled
      ? css`
          cursor: not-allowed;
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: ${theme.colors.dropdownActiveBackground};
            color: ${theme.colors.textBright};
          }
        `}

  ${({ isActive }) => css`
    opacity: ${isActive ? 1 : 0};
    transform: translate3d(${isActive ? 0 : -20}px, 0, 0);
  `};
`;

export const DropdownItem = ({
  children,
  index = 1,
  isActive = false,
  isDisabled,
  isProItem,
  onClick,
}: DropdownItemProps) => {
  if (isProItem)
    return (
      <StyledProContainer
        index={index + 1}
        isDisabled={isDisabled}
        onClick={onClick}
        role="menuitem"
        tabIndex={isActive ? 0 : -1}
      >
        {children}
      </StyledProContainer>
    );

  return (
    <StyledContainer
      index={index + 1}
      isActive={isActive}
      isDisabled={isDisabled}
      onClick={onClick}
      role="menuitem"
      tabIndex={isActive ? 0 : -1}
    >
      {children}
    </StyledContainer>
  );
};
